import { useState } from 'react';
import { ShieldCheck, Trash, UserPlus, Users } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { RequireRole } from '@/features/auth/RequireRole';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDate } from '@/lib/format';
import { selectUser, useAuthStore } from '@/stores/auth-store';
import { roleLabel } from '@/theme';
import type { Role } from '@/types/domain';
import type { Member } from '@/types/settings';
import { MemberDialog } from './MemberDialog';
import { useMembers, useRemoveMember, useUpdateMemberRole } from './use-members';

/**
 * Equipo del negocio (pestaña "Equipo" de `/settings`, módulo 1).
 *
 * Solo ADMIN: el backend protege TODAS las rutas `.../users` con `requireRole('ADMIN')`,
 * incluido el `GET`, así que un agente humano no puede ni listar (403). Por eso la pestaña se
 * envuelve en `RequireRole` (que muestra una explicación en vez de un error de la API) y las
 * acciones de escritura van directas contra los endpoints.
 *
 * Reglas de negocio del backend que la pantalla muestra pero no reimplementa:
 *  - no se puede degradar ni eliminar al ÚLTIMO administrador del tenant (400);
 *  - el email debe ser de una cuenta ya registrada (404) y no repetirse en el tenant (409).
 */

export function TeamTab() {
  return (
    <RequireRole role="ADMIN">
      <TeamPanel />
    </RequireRole>
  );
}

function TeamPanel() {
  const { tenantId } = useActiveTenantAccess();
  const currentUser = useAuthStore(selectUser);

  const membersQuery = useMembers(tenantId);
  const updateMemberRole = useUpdateMemberRole(tenantId);
  const removeMember = useRemoveMember(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<Member | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const members = membersQuery.data ?? [];
  const adminCount = members.filter((member) => member.role === 'ADMIN').length;

  async function changeRole(member: Member, role: Role) {
    if (role === member.role) return;

    setActionError(null);
    try {
      await updateMemberRole.mutateAsync({ userId: member.userId, role });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function confirmRemove() {
    if (!pendingRemove) return;

    setActionError(null);
    try {
      await removeMember.mutateAsync(pendingRemove.userId);
      setPendingRemove(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <Button size="sm" leftIcon={<UserPlus size={14} />} onClick={() => setDialogOpen(true)}>
              Agregar miembro
            </Button>
          }
        >
          <CardTitle>Equipo</CardTitle>
          <CardDescription>
            Quién puede entrar a este negocio y con qué rol. Hay {members.length}{' '}
            {members.length === 1 ? 'miembro' : 'miembros'} y {adminCount}{' '}
            {adminCount === 1 ? 'administrador' : 'administradores'}. El backend no deja quedarse
            sin ningún administrador.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {actionError ? <Alert>{actionError}</Alert> : null}

          {membersQuery.isPending ? (
            <PageLoader label="Cargando el equipo…" />
          ) : membersQuery.isError ? (
            <Alert>{getErrorMessage(membersQuery.error)}</Alert>
          ) : members.length === 0 ? (
            <EmptyState
              icon={<Users size={22} />}
              title="Este negocio no tiene miembros"
              description="Debería estar al menos tú: si ves esto, recarga la página. Para sumar a alguien, la persona debe tener cuenta y se agrega con su email."
              action={
                <Button leftIcon={<UserPlus size={16} />} onClick={() => setDialogOpen(true)}>
                  Agregar miembro
                </Button>
              }
            />
          ) : (
            <CardTableArea className="-mx-6 -mb-6">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Miembro</TableHeaderCell>
                    <TableHeaderCell>Rol</TableHeaderCell>
                    <TableHeaderCell>Desde</TableHeaderCell>
                    <TableHeaderCell align="right">Acciones</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {members.map((member) => {
                    const isMe = member.userId === currentUser?.id;

                    return (
                      <TableRow key={member.userId} className="hover:bg-surface-muted/60">
                        <TableCell>
                          <span className="flex items-center gap-3">
                            <Avatar name={member.nombre ?? member.email} size="sm" />

                            <span className="min-w-0">
                              <span className="block truncate font-medium text-ink">
                                {member.nombre ?? member.email}
                                {isMe ? (
                                  <span className="ml-1.5 text-caption font-normal text-ink-muted">
                                    (tú)
                                  </span>
                                ) : null}
                              </span>
                              <span className="block truncate text-caption text-ink-muted">
                                {member.email}
                              </span>
                            </span>
                          </span>
                        </TableCell>

                        <TableCell>
                          <span className="flex items-center gap-2">
                            <Select
                              id={`rol-${member.userId}`}
                              aria-label={`Rol de ${member.email}`}
                              className="w-44"
                              value={member.role}
                              disabled={updateMemberRole.isPending}
                              onChange={(event) =>
                                void changeRole(member, event.target.value as Role)
                              }
                            >
                              <option value="ADMIN">{roleLabel.ADMIN}</option>
                              <option value="AGENTE_HUMANO">{roleLabel.AGENTE_HUMANO}</option>
                            </Select>

                            {member.role === 'ADMIN' ? (
                              <Badge tone="brand" icon={<ShieldCheck size={12} />}>
                                Gestiona el negocio
                              </Badge>
                            ) : null}
                          </span>
                        </TableCell>

                        <TableCell className="tabular whitespace-nowrap text-caption text-ink-muted">
                          {formatDate(member.joinedAt)}
                        </TableCell>

                        <TableActionsCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Quitar a ${member.email} del negocio`}
                            disabled={removeMember.isPending}
                            onClick={() => {
                              setActionError(null);
                              setPendingRemove(member);
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </TableActionsCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardTableArea>
          )}

          <p className="text-micro text-ink-faint">
            Si te quitas el rol de administrador y queda otro, pierdes el acceso a esta pantalla
            (Ajustes e Integraciones) hasta que te lo devuelvan.
          </p>
        </CardContent>
      </Card>

      <MemberDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

      {/* Confirmación de quitar: el error del backend (p. ej. "No puedes eliminar al último
          administrador del tenant") se muestra dentro del diálogo. */}
      <ConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        title="Quitar del negocio"
        description={
          pendingRemove
            ? `«${pendingRemove.nombre ?? pendingRemove.email}» dejará de tener acceso a este negocio. Su cuenta sigue existiendo: solo se elimina la membresía.`
            : ''
        }
        confirmLabel="Quitar del negocio"
        loading={removeMember.isPending}
        error={actionError}
      />
    </>
  );
}


